import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WifiOff } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { getQueuedCheckin, clearQueuedCheckin } from '@/lib/offlineCheckin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEXTS = {
  offline: {
    fr: 'Pas de connexion. Vos actions reprendront au retour du réseau.',
    ru: 'Нет соединения. Действия возобновятся при появлении сети.',
    md: 'Fără conexiune. Acțiunile vor relua când revine rețeaua.',
    uk: 'Немає з\'єднання. Дії відновляться, коли з\'явиться мережа.',
  },
  sent: {
    fr: 'Votre pointage hors ligne a été envoyé !',
    ru: 'Ваша офлайн-отметка отправлена!',
    md: 'Pontajul dvs. offline a fost trimis!',
    uk: 'Вашу офлайн-відмітку надіслано!',
  },
  already: {
    fr: 'Vous aviez déjà pointé aujourd\'hui.',
    ru: 'Вы уже отметились сегодня.',
    md: 'V-ați punctat deja astăzi.',
    uk: 'Ви вже відмітились сьогодні.',
  },
  expired: {
    fr: 'Le pointage hors ligne datait d\'un autre jour, il n\'a pas pu être envoyé.',
    ru: 'Офлайн-отметка была за другой день, её не удалось отправить.',
    md: 'Pontajul offline era din altă zi, nu a putut fi trimis.',
    uk: 'Офлайн-відмітка була за інший день, її не вдалося надіслати.',
  },
};

export default function OfflineBanner() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);

  const flushQueuedCheckin = useCallback(async () => {
    const item = getQueuedCheckin();
    if (!item || !user) return;
    if (item.userId !== user.id) {
      clearQueuedCheckin();
      return;
    }
    try {
      await axios.post(`${API}/attendance/checkin`, item.payload);
      clearQueuedCheckin();
      toast.success(TEXTS.sent[language] || TEXTS.sent.fr, { duration: 8000 });
      // Refresh so dashboard/attendance reflect the new state
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) {
      if (!e.response) return; // still offline, keep the entry
      clearQueuedCheckin();
      if (e.response.status === 400) {
        toast.success(TEXTS.already[language] || TEXTS.already.fr);
      } else {
        toast.warning(TEXTS.expired[language] || TEXTS.expired.fr, { duration: 8000 });
      }
    }
  }, [user, language]);

  useEffect(() => {
    const goOnline = () => {
      setOffline(false);
      flushQueuedCheckin();
    };
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [flushQueuedCheckin]);

  // On login / app start, try to flush anything left from a previous session
  useEffect(() => {
    if (user && navigator.onLine) {
      const t = setTimeout(flushQueuedCheckin, 2000);
      return () => clearTimeout(t);
    }
  }, [user, flushQueuedCheckin]);

  if (!offline) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-slate-900 text-center text-sm font-semibold py-2 px-4 flex items-center justify-center gap-2 shadow-md"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      data-testid="offline-banner"
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" />
      <span>{TEXTS.offline[language] || TEXTS.offline.fr}</span>
    </div>
  );
}
